import React, {useEffect, useState} from 'react';
import {Card, CardContent, Button, Typography, Box, CircularProgress, LinearProgress} from '@mui/material';
import Icon1 from '../../../assets/images/DQProImages/IconImg1.png';
import Icon2 from '../../../assets/images/DQProImages/IconImg2.png';
import Icon4 from '../../../assets/images/DQProImages/IconImg4.png';
import useStyles from '../../../assets/styles/ProgramCreatorStyles';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {
    getInsiderProgramService,
    getLearningProgramPreviewStatusService, getLearningProgramStatusService
} from "../../../services/learning_programs.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";

const OUTLINE_STEPS = [
    "Generating program modules and descriptions.",
    "Generating program units and descriptions.",
    "Generating program sessions and descriptions.",
    "Generating program session learning objectives.",
    "Applying learner context.",
    "Organizing program outline.",
    "Mapping with DQ Codes.",
    "Identifying related frameworks."
];

const STATUS_TO_STEP_INDEX = {
    1: 1, // steps 0-1 → "modules" + "units"
    2: 3, // steps 2-3 → "sessions" + "learning objectives"
    3: 5, // steps 4-5 → "learner context" + "organizing outline"
    4: 6, // step 6 → "mapping with DQ Codes"
    5: 7, // step 7 → "identifying related frameworks"
};

function deriveStepIndexFromStatus(progressStatus) {
    if (!progressStatus || progressStatus < 1) return 0;
    return STATUS_TO_STEP_INDEX[Math.min(progressStatus, 5)] ?? 0;
}

function derivePercentFromStatus(isReady, progressStatus) {
    if (isReady) return 100;
    // Spread 1..5 across 95% (reserve the last 5% for the final "ready" flip)
    if (!progressStatus) return 0; // not started yet
    const clamped = Math.max(1, Math.min(progressStatus, 5));
    return Math.round((clamped / 5) * 95);
}

const DQProProgDashboardPage = () => {
    const classes = useStyles();
    const location = useLocation();
    const history = useHistory();
    const { code } = useParams();
    const [programData, setProgramData] = useState(location.state?.assessment || null);
    const [loading, setLoading] = useState(!location.state?.assessment);
    const [outlineStatus, setOutlineStatus] = useState({
        is_outline_ready: null,
        generation_initiated: null,
        failed: false,
        failure_reason: ''
    });
    const [previewEnabled, setPreviewEnabled] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [dqcoList, setDqcoList] = useState(location.state?.dqcoList || []);

    // local UI progress for outline generation
    const [outlineStepIndex, setOutlineStepIndex] = useState(0);
    const [outlineProgress, setOutlineProgress] = useState(0);


    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                if (!programData && code) {
                    setLoading(true);
                    const response = await getInsiderProgramService(code);
                    if (response?.data?.learning_program) {
                        setProgramData(response.data.learning_program);
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData().then(r => console.log(r));
    }, [programData, code]);

    useEffect(() => {
        const fetchAllStatuses = async () => {
            setLoading(true);
            setPreviewLoading(false);
            setPreviewEnabled(false);
            try {
                // Outline status
                const outlineRes = await getLearningProgramPreviewStatusService(code);
                const isOutlineReady = outlineRes?.data?.is_outline_ready;
                const generationInitiated = outlineRes?.data?.generation_initiated;
                const progressStatus = outlineRes?.data?.progress_status; // 1..5
                const failureReason = outlineRes?.data?.error || '';
                const failed = progressStatus === -1;
                setOutlineStatus({
                    is_outline_ready: isOutlineReady,
                    generation_initiated: generationInitiated,
                    failed,
                    failure_reason: failureReason
                });

                // Drive UI from API
                if (failed) {
                    setOutlineStepIndex(0);
                    setOutlineProgress(0);
                } else {
                    setOutlineStepIndex(isOutlineReady ? OUTLINE_STEPS.length : deriveStepIndexFromStatus(progressStatus));
                    setOutlineProgress(derivePercentFromStatus(isOutlineReady, progressStatus));
                }

                // Preview status
                if (generationInitiated) {
                    setPreviewLoading(true);
                    const contentRes = await getLearningProgramStatusService(code);
                    const isM1U1S1Done = contentRes?.data?.contentStatus?.['M1_U1_S1'] === 'done';
                    setPreviewEnabled(isM1U1S1Done);
                    setPreviewLoading(!isM1U1S1Done);
                }
            } catch (e) {
                // Handle error
            } finally {
                setLoading(false);
            }
        };

        if (code) fetchAllStatuses();
    }, [code]);

    // Polling effect
    useEffect(() => {
        let intervalId;
        if (code && !outlineStatus.failed && (outlineStatus.is_outline_ready !== true || outlineStatus.generation_initiated !== true)) {
            intervalId = setInterval(async () => {
                const response = await getLearningProgramPreviewStatusService(code);
                const isReady = response?.data?.is_outline_ready;
                const initiated = response?.data?.generation_initiated;
                const progressStatus = response?.data?.progress_status; // 1..5
                const failureReason = response?.data?.error || '';

                // Failure: mark failed and stop polling
                if (progressStatus === -1) {
                    setOutlineStatus({
                        is_outline_ready: isReady,
                        generation_initiated: initiated,
                        failed: true,
                        failure_reason: failureReason
                    });
                    setOutlineStepIndex(0);
                    setOutlineProgress(0);
                    clearInterval(intervalId);
                    return;
                }

                setOutlineStatus({
                    is_outline_ready: isReady,
                    generation_initiated: initiated,
                    failed: false,
                    failure_reason: ''
                });

                setOutlineStepIndex(
                    isReady ? OUTLINE_STEPS.length : deriveStepIndexFromStatus(progressStatus)
                        );
                setOutlineProgress(
                    derivePercentFromStatus(isReady, progressStatus)
                    );
                if (response?.data?.is_outline_ready && response?.data?.generation_initiated) {
                    clearInterval(intervalId);
                }
            }, 5000);
        }
        return () => clearInterval(intervalId);
    }, [code, outlineStatus.is_outline_ready, outlineStatus.generation_initiated, outlineStatus.failed]);

    useEffect(() => {
        let intervalId;

        if (
            outlineStatus.generation_initiated &&
            !outlineStatus.failed &&
            !previewEnabled // Only poll if not yet enabled
        ) {
            setPreviewLoading(true);
            intervalId = setInterval(async () => {
                try {
                    const contentRes = await getLearningProgramStatusService(code);
                    const isM1U1S1Done = contentRes?.data?.contentStatus?.['M1_U1_S1'] === 'done';
                    setPreviewEnabled(isM1U1S1Done);
                    setPreviewLoading(!isM1U1S1Done);
                    if (isM1U1S1Done) clearInterval(intervalId);
                } catch (e) {
                    // Optionally handle
                }
            }, 10000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [code, outlineStatus.generation_initiated, previewEnabled, outlineStatus.failed]);

    const cardsData = [
        {
            icon: Icon1,
            title: 'Program Outline',
            content: 'Overview of your curated program',
            buttons: [
                {
                    label: 'View/Edit',
                    url: `/dq_pro/products/program/${code}/outline`
                }
            ],
            height: '40px',
            width: '70px'
        },
        {
            icon: Icon2,
            title: 'Your Program',
            content: 'Preview your ready-to-go online program',
            buttons: [
                {
                    label: 'Preview',
                    url: `/dq_pro/products/program/${code}/preview`
                }
            ],
            height: '45px',
            width: '55px'
        },
        {
            icon: Icon4,
            title: 'Resources',
            content: 'Everything you need to run your program online, offline, or integrate with your system via API',
            buttons: [
                {
                    label: 'Access',
                    url: '/program/123/resources'
                }
            ],
            height: '45px',
            width: '52px'
        }
    ];

    const activeStep = 1;

    const handleNavigation = (url) => {
      history.push({
          pathname: url,
          state: programData
      })
    };

    return (
        <div className={classes.container}>
            <Typography variant="h4" className={classes.title}>
                Here’s your program!
            </Typography>
            <Box sx={{flexDirection:{xs:'column', sm:'row'}, padding: {xs:'0px',md:'0px',lg:'0px 110px'}, alignItems:{xs:'center',}, }} style={{ display: 'flex', justifyContent: 'space-around', }}>
                {cardsData.map((step, index) => {
                    let showLoader = false;
                    let disabled = true;

                    if (index === 0) { // Program Outline
                        showLoader = outlineStatus.is_outline_ready !== true;
                        disabled = outlineStatus.is_outline_ready !== true;
                    } else if (index === 1) { // Program Preview
                        showLoader = previewLoading;
                        disabled = !previewEnabled || outlineStatus.is_outline_ready !== true;
                    }
                    return (
                    <Card
                        key={index}
                        className={`${classes.card}`}
                        style={{
                            pointerEvents: index <= activeStep ? 'auto' : 'none',
                            position: 'relative',
                                transition: 'opacity 0.3s ease, filter 0.3s ease'
                        }}
                    >
                            {(disabled || showLoader) && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(202,199,199,0.41)',
                                backdropFilter: 'blur(2px)',
                                zIndex: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 1,
                                filter: 'none'
                            }} />
                        )}

                        {/* Loader overlay */}
                        {showLoader && !outlineStatus.failed && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: showLoader ? 'rgba(202,199,199,0.7)' : 'rgba(202,199,199,0.41)',
                                    backdropFilter: 'blur(2px)',
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 1,
                                    filter: 'none'
                                }}
                            >
                                    {showLoader && (
                                        <>
                                            <CircularProgress sx={{ color: '#ff6468' }}/>
                                            {index === 0 ? (
                                                <>
                                                    {/* Active step only */}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 1,
                                                            color: outlineStatus.is_outline_ready ? '#2e7d32' : '#ff6468',
                                                            fontWeight: 700,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {outlineStatus.is_outline_ready
                                                            ? 'Outline ready. Finalizing…'
                                                            : OUTLINE_STEPS[Math.min(outlineStepIndex, OUTLINE_STEPS.length - 1)]
                                                        }
                                                    </Typography>
                                                    <Box sx={{ width: '80%', mb: 1 }}>
                                                        <LinearProgress variant="determinate" value={outlineProgress} sx={{ height: 10, borderRadius: 5 }} />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ mb: 2, color: '#333', fontWeight: 600 }}>
                                                        {Math.round(outlineProgress)}%
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            mt: 2,
                                                            color: '#ff6468',
                                                            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                            fontWeight: 700,
                                                            fontSize:'20px',
                                                            textAlign: 'center',
                                                            maxWidth: '80%',
                                                        }}
                                                    >
                                                        Please wait. Your program content is being generated.
                                                    </Typography>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            mt: 2,
                                                            color: '#ff6468',
                                                            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                            fontWeight: 700,
                                                            textAlign: 'center',
                                                            fontSize:'12px',
                                                            maxWidth: '80%',
                                                        }}
                                                    >
                                                        (Take note that images for your content may take an additional few minutes to appear in the program slides after the text has been generated.)
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                            </Box>
                        )}
                        {outlineStatus.failed && index === 0 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0, left: 0, width: '100%', height: '100%',
                                    backgroundColor: 'rgba(255, 235, 238, 0.95)',
                                    zIndex: 3,
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                    px: 3, textAlign: 'center'
                                }}
                            >
                                <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 800, mb: 1 }}>
                                    Outline generation failed
                                </Typography>
                                {outlineStatus.failure_reason ? (
                                    <Typography variant="body2" sx={{ color: '#b71c1c', mb: 2 }}>
                                        {outlineStatus.failure_reason}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                        <CardContent className={classes.cardContent}>
                            <div className={classes.cardHeader}>
                                <img src={step.icon} alt={step.title}
                                     style={{width: step.width, height: step.height, marginBottom: '20px', marginTop: '20px'}}/>
                                <Typography variant="h5"
                                            style={{color: '#fff', marginBottom: '10px'}}>{step.title}</Typography>
                            </div>
                            <Typography variant="body1" style={{
                                display: index === 0 && showLoader ? 'none' : 'block',
                                marginTop: '50px',
                                padding: '0 10px',
                                textAlign: 'center'
                            }}>{step.content}</Typography>

                            {index <= activeStep && (
                                <Box sx={{flexDirection:{sm:'row', xs:'column'}}} style={{display: 'flex', position: 'absolute', bottom: '25px', width:'95%',}}>
                                    {step.buttons.map((button, btnIndex) => (
                                            <Button
                                                key={btnIndex}
                                                className={classes.button}
                                                variant="contained"
                                                onClick={() => {
                                                    if (button.onClick) {
                                                        button.onClick();
                                                    } else if (button.url) {
                                                        history.push({
                                                            pathname: button.url,
                                                            state: {
                                                                assessment: programData,
                                                                outlineStatus: outlineStatus,   // 👈 Pass the outline status!
                                                                dqcoList: dqcoList
                                                            }
                                                        });
                                                    }
                                                }}
                                                disabled={disabled}
                                            >
                                                {button.label}
                                            </Button>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                    )
                })}
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </div>
    );
};

export default DQProProgDashboardPage;